import { useQuery } from '@tanstack/react-query';
import { AlertTriangleIcon } from 'lucide-react';
import { useEffect, useState, type JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { Checkbox } from '@/components/ui/checkbox.js';
import {
  projectQueryOptions,
  qualityOperationVersionUnsupported,
  supportsQualityOperationFields,
  useProjectEnvironments,
  useProjectLaunchOptions,
  useQualityCapabilities,
  type RelaunchRunBody,
} from '@/lib/api/quality.js';
import type {
  CaseOutcome,
  Platform,
  TestResult,
  WebNetworkCaptureOverride,
} from '@/lib/api/quality-types.js';
import { EnvironmentSelect } from './EnvironmentSelect.js';
import {
  environmentSelectionBody,
  LaunchEnvironmentSelect,
  LaunchPlatformSelect,
  platformSelectionBody,
  type LaunchEnvironmentSelection,
  type LaunchPlatformSelection,
} from './LaunchSettingsSelectors.js';
import {
  normalizeRunExecutionMode,
  RunAdvancedSettings,
} from './RunAdvancedSettings.js';
import type { RunExecutionMode } from '@/lib/api/quality-types.js';
import {
  DEFAULT_RESTART_OUTCOMES,
  caseDisplayStatus,
  caseOutcome,
  outcomeLabel,
} from './quality-outcome.js';

const CANONICAL_RELAUNCH_FIELDS = [
  'environment_mode',
  'environment_name',
  'target_platforms',
  'human_interaction_enabled',
] as const;

/** Relaunch inherits an effective snapshot, so every legacy or future wire value fails safe to the
 * historical effective default instead of leaving the controlled Select with no matching item. */
function normalizeRelaunchWebNetworkCaptureMode(
  value: unknown,
): WebNetworkCaptureOverride {
  return value === 'errors-only' ? 'errors-only' : 'full-har';
}

export function RelaunchRunDialog({
  projectId,
  mode,
  pending,
  reportChanged = false,
  onClose,
  onLaunch,
  initialEnvironmentName,
  initialRequestedPlatforms,
  initialHumanInteractionEnabled,
  initialExecutionMode,
  initialWebNetworkCaptureMode,
  webNetworkCaptureAvailable = false,
  restartCandidates,
}: {
  projectId?: string;
  initialEnvironmentName?: string | null;
  initialRequestedPlatforms?: Platform[] | null;
  initialHumanInteractionEnabled?: boolean;
  /** The source run's case execution mode; a rerun keeps it, so it is shown read-only. */
  initialExecutionMode?: RunExecutionMode;
  initialWebNetworkCaptureMode?: WebNetworkCaptureOverride;
  webNetworkCaptureAvailable?: boolean;
  mode: 'restart' | 'rerun';
  pending: boolean;
  reportChanged?: boolean;
  onClose: () => void;
  onLaunch: (settings: RelaunchRunBody) => void;
  restartCandidates?: TestResult[];
}): JSX.Element {
  const resolvedProjectId = projectId ?? '';
  const project = useQuery(projectQueryOptions(resolvedProjectId));
  const capabilities = useQualityCapabilities();
  const operation =
    mode === 'restart' ? 'quality.restart_failed' : 'quality.rerun_all';
  const supportsCanonicalRelaunch = supportsQualityOperationFields(
    capabilities.data,
    operation,
    CANONICAL_RELAUNCH_FIELDS,
  );
  const [canonicalRelaunchSeen, setCanonicalRelaunchSeen] = useState(false);
  useEffect(() => {
    if (supportsCanonicalRelaunch) setCanonicalRelaunchSeen(true);
  }, [supportsCanonicalRelaunch]);
  const canonicalRelaunch =
    Boolean(projectId) && (supportsCanonicalRelaunch || canonicalRelaunchSeen);
  // An operation advertised at a version this client cannot speak refuses every relaunch, so the
  // established form would never submit: block from the start instead.
  const relaunchVersionUnsupported = qualityOperationVersionUnsupported(
    capabilities.data,
    operation,
  );
  const launchContractChanged =
    relaunchVersionUnsupported ||
    (canonicalRelaunch && !supportsCanonicalRelaunch);
  const launchOptions = useProjectLaunchOptions(
    resolvedProjectId,
    canonicalRelaunch,
  );
  const { environments, defaultEnvironmentName } =
    useProjectEnvironments(resolvedProjectId);
  const [legacyEnvironment, setLegacyEnvironment] = useState<{
    name: string | undefined;
  }>();
  const [environmentSelection, setEnvironmentSelection] =
    useState<LaunchEnvironmentSelection>({ mode: 'inherit' });
  const [platformSelection, setPlatformSelection] =
    useState<LaunchPlatformSelection>('inherit');
  const [humanInteractionEnabled, setHumanInteractionEnabled] = useState<
    boolean | null
  >(null);
  const legacyEnvironmentName = legacyEnvironment
    ? legacyEnvironment.name
    : initialEnvironmentName !== undefined
      ? (initialEnvironmentName ?? undefined)
      : environments.find(
          (environment) => environment.name === defaultEnvironmentName,
        )?.name;
  /** Whether the user has moved off "keep whatever the source run used" for the environment — in
   *  either selector. Gates the re-read-vs-replayed caveat below. */
  const environmentSelectionChanged = canonicalRelaunch
    ? environmentSelection.mode !== 'inherit'
    : legacyEnvironmentName !== (initialEnvironmentName ?? undefined);
  /** True when the changed selection is explicitly "no environment" — `mode: 'none'` on the
   *  canonical selector, or `undefined` on the legacy one. That selection substitutes nothing, so
   *  the caveat must not promise resolution "against this [environment]" for it. */
  const environmentSelectionIsNone = canonicalRelaunch
    ? environmentSelection.mode === 'none'
    : legacyEnvironmentName === undefined;
  const inheritedHumanInteraction = canonicalRelaunch
    ? (initialHumanInteractionEnabled ??
      launchOptions.data?.effective_settings.human_interaction_enabled ??
      false)
    : project.data?.settings?.hitlMode === 'all';
  const [selectionIntent, setSelectionIntent] = useState<Map<string, boolean>>(
    () => new Map(),
  );
  const [webNetworkCaptureMode, setWebNetworkCaptureMode] =
    useState<WebNetworkCaptureOverride>(
      normalizeRelaunchWebNetworkCaptureMode(initialWebNetworkCaptureMode),
    );
  const title =
    mode === 'restart'
      ? restartCandidates
        ? 'Restart cases'
        : 'Restart failed'
      : 'Rerun again';
  const selected = (restartCandidates ?? []).filter((result) => {
    const explicitSelection = selectionIntent.get(result.case_id);
    if (explicitSelection !== undefined) return explicitSelection;
    const outcome = caseOutcome(result);
    return (
      outcome !== null &&
      DEFAULT_RESTART_OUTCOMES.some((candidate) => candidate === outcome)
    );
  });
  const safeProjectionUnavailable =
    canonicalRelaunch && (launchOptions.isError || !launchOptions.data);

  const launch = (): void => {
    if (launchContractChanged) return;
    const includeOutcomes = [
      ...new Set(
        selected.flatMap((result) => {
          const outcome = caseOutcome(result);
          return outcome === 'defect_found' || outcome === 'case_unclear'
            ? [outcome]
            : [];
        }),
      ),
    ] as Extract<CaseOutcome, 'defect_found' | 'case_unclear'>[];
    onLaunch({
      ...(projectId
        ? canonicalRelaunch
          ? {
              ...environmentSelectionBody(environmentSelection),
              ...platformSelectionBody(platformSelection, true),
              ...(humanInteractionEnabled !== null
                ? { humanInteractionEnabled }
                : {}),
            }
          : {
              environmentName: legacyEnvironmentName ?? '',
              humanInteractionEnabled:
                humanInteractionEnabled ??
                project.data?.settings?.hitlMode === 'all',
            }
        : {}),
      ...(webNetworkCaptureAvailable ? { webNetworkCaptureMode } : {}),
      ...(mode === 'restart' && restartCandidates
        ? {
            caseIds: selected.map((result) => result.case_id),
            ...(includeOutcomes.length > 0 ? { includeOutcomes } : {}),
          }
        : {}),
    });
  };
  return (
    <AppDialog
      open
      onOpenChange={(open) => {
        if (!open && !pending) onClose();
      }}
      title={title}
      description={
        mode === 'restart'
          ? 'Choose settings for a new run of the failed cases.'
          : 'Choose settings for a new run of all cases.'
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-xs text-muted-foreground">
          Runs each case's current text where its source can still be read, or
          the recorded snapshot otherwise.
        </p>
        {mode === 'restart' && restartCandidates ? (
          <div className="space-y-2">
            <p className="text-sm font-medium">Cases to restart</p>
            {restartCandidates.length === 0 ? (
              <p className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground">
                No cases in the current outcome revision can be restarted.
              </p>
            ) : (
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-border p-2">
                {restartCandidates.map((result) => {
                  const outcome = caseOutcome(result);
                  if (!outcome) return null;
                  const explicitSelection = selectionIntent.get(result.case_id);
                  const checked =
                    explicitSelection ??
                    DEFAULT_RESTART_OUTCOMES.some(
                      (candidate) => candidate === outcome,
                    );
                  return (
                    <label
                      key={result.case_id}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted/40"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(next) =>
                          setSelectionIntent((current) => {
                            const changed = new Map(current);
                            changed.set(result.case_id, next === true);
                            return changed;
                          })
                        }
                      />
                      <span className="min-w-0 flex-1 truncate">
                        {result.title || result.case_id}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {outcomeLabel(caseDisplayStatus(result) as CaseOutcome)}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              QA, infrastructure, and input-blocked cases are selected by
              default. Defects and unclear cases require explicit selection.
            </p>
            <p className="text-sm font-medium" aria-live="polite">
              {selected.length} of {restartCandidates.length} selected
            </p>
          </div>
        ) : null}
        {projectId ? (
          <>
            {capabilities.isLoading ? (
              <p className="text-xs text-muted-foreground">
                Checking supported relaunch settings…
              </p>
            ) : launchContractChanged ? (
              <div role="alert" className="space-y-2 text-xs text-destructive">
                <p>
                  Supported relaunch settings changed. Your choices are kept.
                </p>
                <Button
                  variant="outline"
                  disabled={capabilities.isFetching}
                  onClick={() => void capabilities.refetch()}
                >
                  Refresh settings
                </Button>
              </div>
            ) : !canonicalRelaunch ? (
              <div className="flex items-start gap-2 rounded-lg border border-warning-500/40 bg-warning-500/10 px-3 py-2 text-xs text-warning-600">
                <AlertTriangleIcon
                  className="mt-0.5 size-3.5 shrink-0"
                  aria-hidden
                />
                <p>
                  Inherit, explicit default, and platform controls are
                  unavailable on this Quality service. Relaunch uses the
                  existing settings behavior.
                </p>
              </div>
            ) : launchOptions.isError ? (
              <p className="text-xs text-destructive">
                Safe relaunch options could not be loaded. Retry before
                relaunching.
              </p>
            ) : null}

            {canonicalRelaunch && launchOptions.data ? (
              <>
                <LaunchEnvironmentSelect
                  launchOptions={launchOptions.data}
                  value={environmentSelection}
                  onChange={setEnvironmentSelection}
                  inheritedName={initialEnvironmentName ?? null}
                />
                <LaunchPlatformSelect
                  launchOptions={launchOptions.data}
                  value={platformSelection}
                  onChange={setPlatformSelection}
                  inheritedPlatforms={initialRequestedPlatforms ?? null}
                />
              </>
            ) : (
              <EnvironmentSelect
                projectId={projectId}
                value={legacyEnvironmentName}
                onChange={(name) => setLegacyEnvironment({ name })}
              />
            )}

            {environmentSelectionChanged ? (
              <p className="text-xs text-muted-foreground">
                {environmentSelectionIsNone
                  ? 'A case re-read from its source resolves URLs and values with no environment substituted. A case replayed from the recorded snapshot keeps the values already substituted for the previous environment.'
                  : 'A case re-read from its source resolves URLs and values against the newly selected environment. A case replayed from the recorded snapshot keeps the values already substituted for the previous environment; launch the suite again to resolve it against this one.'}
              </p>
            ) : null}

            <RunAdvancedSettings
              inheritedTakeoverEnabled={inheritedHumanInteraction}
              inheritanceLabel={canonicalRelaunch ? 'source run' : 'workspace'}
              value={humanInteractionEnabled}
              onChange={setHumanInteractionEnabled}
              executionOrder={
                initialExecutionMode
                  ? {
                      inherited:
                        normalizeRunExecutionMode(initialExecutionMode),
                      value: null,
                    }
                  : undefined
              }
              webNetworkCapture={
                webNetworkCaptureAvailable
                  ? {
                      value: webNetworkCaptureMode,
                      onChange: setWebNetworkCaptureMode,
                    }
                  : undefined
              }
            />

            {project.isError && !canonicalRelaunch ? (
              <p className="text-sm text-destructive">
                Could not load project settings. Close this dialog and try
                again.
              </p>
            ) : null}
          </>
        ) : webNetworkCaptureAvailable ? (
          <RunAdvancedSettings
            webNetworkCapture={{
              value: webNetworkCaptureMode,
              onChange: setWebNetworkCaptureMode,
              defaultLabel: 'Use system default',
            }}
          />
        ) : null}
        {reportChanged ? (
          <p className="rounded-md border border-warning-500/30 bg-warning-500/5 p-3 text-sm text-warning-700">
            The report changed while this dialog was open. Your selections have
            been kept, and launching will be available after the current
            revision loads.
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" disabled={pending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="brand"
            disabled={
              pending ||
              reportChanged ||
              launchContractChanged ||
              (Boolean(projectId) && capabilities.isLoading) ||
              (Boolean(projectId) &&
                (canonicalRelaunch
                  ? safeProjectionUnavailable ||
                    launchOptions.data?.permissions.can_run !== true
                  : !project.data)) ||
              (Boolean(projectId) && project.data?.can_run === false) ||
              (mode === 'restart' &&
                restartCandidates !== undefined &&
                selected.length === 0)
            }
            onClick={launch}
          >
            {pending ? 'Launching…' : title}
          </Button>
        </div>
        {projectId &&
        (project.data?.can_run === false ||
          (canonicalRelaunch &&
            launchOptions.data?.permissions.can_run === false)) ? (
          <p className="text-sm text-muted-foreground">
            You can review this run, but you do not have permission to launch a
            restart.
          </p>
        ) : null}
      </div>
    </AppDialog>
  );
}
